import React from 'react';
import styles from './styles.module.scss';
import OfferCard from './offer-card';
import NoData from '../no-data';
import {toaster} from 'components/chakra-ui/toaster';
import {Platform} from 'enums/devices';
import {useFilterContext} from 'providers/filter-context';

type CardListProps<T> = {
  data: T | undefined;
  isFetching: boolean;
};

const CardList = <T extends any[]>({data, isFetching}: CardListProps<T>): React.ReactElement => {
  const {filterType} = useFilterContext();
  
  // Check if any filters are selected
  const hasSelectedFilters = Object.values(filterType).some(value => value);

  const handleBlurredCardClick = () => {
    toaster.create({
      description: "Unlock this offer by completing the other offers first.",
      duration: 5000,
      type: 'info',
      action: {
        label: 'Close',
        onClick: () => {}
      }
    });
  };

  const lockedOffers = [
    // Android Games
    {
      id: 'locked-android-1',
      game_logo: '/images/offer_logos/kings.jpg',
      game_name: 'Kings Quest Adventures',
      offer: '$185.50',
      type: 'Game',
      platform: 'android',
      isBlurred: true
    },
    {
      id: 'locked-android-2',
      game_logo: '/images/offer_logos/farm.jpg',
      game_name: 'Farm Valley Saga',
      offer: '$225.75',
      type: 'Game',
      platform: 'android',
      isBlurred: true
    },
    {
      id: 'locked-android-3',
      game_logo: '/images/offer_logos/race.jpg',
      game_name: 'Space Star: Speed',
      offer: '$195.25',
      type: 'Game',
      platform: 'android',
      isBlurred: true
    },
    // iOS Games
    {
      id: 'locked-ios-1',
      game_logo: '/images/offer_logos/poker.jpg',
      game_name: 'Poker Tower',
      offer: '$215.50',
      type: 'Game',
      platform: 'ios',
      isBlurred: true
    },
    {
      id: 'locked-ios-2',
      game_logo: '/images/offer_logos/knight.jpg',
      game_name: 'Knight Glory',
      offer: '$175.25',
      type: 'Game',
      platform: 'ios',
      isBlurred: true
    },
    {
      id: 'locked-ios-3',
      game_logo: '/images/offer_logos/breach.jpg',
      game_name: 'Breach: The Fallen',
      offer: '$205.75',
      type: 'Game',
      platform: 'ios',
      isBlurred: true
    },
    // Desktop Games
    {
      id: 'locked-desktop-1',
      game_logo: '/images/offer_logos/prize.jpg',
      game_name: 'Epic Prize Empire',
      offer: '$245.50',
      type: 'Game',
      platform: 'desktop',
      isBlurred: true
    },
    {
      id: 'locked-desktop-2',
      game_logo: '/images/offer_logos/pool.jpg',
      game_name: 'Whole Pool: Online',
      offer: '$235.25',
      type: 'Game',
      platform: 'desktop',
      isBlurred: true
    },
    {
      id: 'locked-desktop-3',
      game_logo: '/images/offer_logos/strike.jpg',
      game_name: 'Strike Master: Tactics 2',
      offer: '$195.75',
      type: 'Game',
      platform: 'desktop',
      isBlurred: true
    }
  ];

  // Filter locked offers based on selected platforms
  const filteredLockedOffers = lockedOffers.filter(offer => {
    switch (offer.platform) {
      case 'android':
        return filterType.android;
      case 'ios':
        return filterType.ios;
      case 'desktop':
        return filterType.desktop;
      default:
        return false;
    }
  });

  // If no filters are selected, show a message
  if (!hasSelectedFilters) {
    return <NoData message="Please select at least one device type to see offers" />;
  }

  return (
    <div className={styles.wrapper}>
      {isFetching ? (
        [1, 2, 3, 4, 5].map((item, index: number) => {
          return <OfferCard loading={isFetching} data={null} key={index} />;
        })
      ) : data?.length && data.length > 0 ? (
        <>
          {data.map((item, index: number) => {
            return <OfferCard key={`item-${index}`} data={item} loading={isFetching} />;
          })}
          {filteredLockedOffers.map((lockedOffer) => (
            <div key={lockedOffer.id} onClick={handleBlurredCardClick} className={styles.blurredCard}>
              <OfferCard 
                data={{
                  offer_id: lockedOffer.id,
                  game_name: lockedOffer.game_name,
                  game_logo: lockedOffer.game_logo,
                  total_rewards_value_str: lockedOffer.offer,
                  platform: lockedOffer.platform as Platform,
                  requirements: '',
                  description: '',
                  game_link: '',
                  game_qr_code: '',
                  offer_sequence: '',
                  popularity_score: 0
                }} 
                loading={false}
                isBlurred={true}
              />
            </div>
          ))}
        </>
      ) : (
        <NoData message="No offers found" />
      )}
    </div>
  );
};

export default CardList;
